import { Download } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { sobService } from '@/services/sobService';

interface SOBPreviewProps {
  caseData: any;
  programId: string;
  summary?: { section_summaries?: Record<string, string> };
}

export const SOBPreview: React.FC<SOBPreviewProps> = ({ caseData, programId, summary }) => {
  const [sobHTML, setSOBHTML] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (caseData && programId) {
      try {
        const html = sobService.generateSOBHTML(caseData, programId, summary);
        setSOBHTML(html);
      } catch (error) {
        console.error('Failed to generate SOB preview:', error);
        setSOBHTML(
          '<div style="padding: 20px; text-align: center;">Failed to generate SOB preview</div>',
        );
      }
    }
  }, [caseData, programId, summary]);

  const handleDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    const toastId = toast.loading('Generating SOB PDF...');

    try {
      await sobService.downloadSOB(caseData, programId, summary);
      toast.success('SOB downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('Failed to generate SOB PDF:', error);
      toast.error('Failed to generate SOB PDF', { id: toastId });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center px-4 py-2 border-b">
        <h4 className="text-muted-foreground font-medium text-sm">Summary of Benefits</h4>
        <SimpleTooltip content={isDownloading ? 'Generating...' : 'Download'} side="bottom">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading || !caseData}
            className="h-5 w-5 p-0"
          >
            <Download className="w-3 h-3" />
          </Button>
        </SimpleTooltip>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        {sobHTML ? (
          <div className="sob-preview-container">
            <style>{`
              .sob-preview-container {
                background: white;
                min-height: 100%;
              }
              .sob-preview-container .sob-container {
                width: 100%;
                max-width: 100%;
                padding: 20px;
              }
              @media print {
                .sob-preview-container .sob-container {
                  width: 210mm;
                  padding: 20mm;
                }
              }
            `}</style>
            <div dangerouslySetInnerHTML={{ __html: sobHTML }} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>No SOB data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
